import {last, mapObjIndexed, propEq} from 'ramda';
import {Event} from './Events';
import {ActiveMarketMap, DEFAULT_MARKET, Market, isValidMarket} from './Markets';
import {FlatPickup} from './Pickups';
import {addDays, isBetweenOrEqual, isoNow} from './Time';

export enum Reason {
  MOVING = 'Moving',
  LIQUIDATION = 'Liquidation',
  REDECORATING = 'Redecorating',
  DECLUTTERING = 'Decluttering',
  ESTATE_SALE = 'Estate Sale',
  DIVORCE = 'Divorce',
  DOWNSIZING = 'Downsizing',
  OTHER = 'Other',
}
export const socialMediaPublicReview = {
  YELP: 'Yelp',
  GOOGLE: 'Google',
  NEXTDOOR: 'Nextdoor',
} as const;
export type SocialMedia = keyof typeof socialMediaPublicReview;
export const SocialMediaPublicReviewMetricName: Record<SocialMedia, string> = {
  YELP: 'yelpReviewClick',
  GOOGLE: 'googleReviewClick',
  NEXTDOOR: 'nextdoorReviewClick',
};
type SocialMediaPublicReview = Record<SocialMedia, string>;
type MarketSocialMediaPublicReview = ActiveMarketMap<SocialMediaPublicReview>;
export const marketSocialMediaPublicReview: MarketSocialMediaPublicReview = {
  [Market.BAY_AREA]: {
    YELP: 'https://www.yelp.com/writeareview/biz/_nBPW10YazYDlLpcAVsWnA',
    GOOGLE: 'https://g.page/r/CShrw1DjfNfHEAg/review',
    NEXTDOOR: 'https://nextdoor.com/login/?next=/pages/remoov/recommend/',
  },
  [Market.PHOENIX]: {
    YELP: 'https://www.yelp.com/writeareview/biz/C7i46JMddQ8oxbOgWuszWw',
    GOOGLE: 'https://g.page/r/CbLi4VJdEkdNEAg/review',
    NEXTDOOR: 'https://nextdoor.com/login/?next=/pages/remoov-tempe-az/recommend/',
  },
  // [Market.SACRAMENTO]: {
  //   YELP: 'https://www.yelp.com/writeareview/biz/r4M8pDl_lbh9qrdGTPtc9A',
  //   GOOGLE: 'https://g.page/r/CTY1HoykcfmHEBM/review',
  //   NEXTDOOR: 'https://nextdoor.com/pages/remoov-carmichael-ca/recommend',
  // },
  [Market.LOS_ANGELES]: {
    YELP: 'https://www.yelp.com/writeareview/biz/tlnpFE3xIpDbXL-Sw92OGA',
    GOOGLE: 'https://g.page/r/CbmnSParm9YhEBM/review',
    NEXTDOOR: 'https://nextdoor.com/login/?next=/pages/remoov-los-angeles-ca/recommend',
  },
};
export function getMarketPublicReviewData(market: Maybe<string>): SocialMediaPublicReview {
  const m = isValidMarket(market) ? market : DEFAULT_MARKET;
  return marketSocialMediaPublicReview[m] || marketSocialMediaPublicReview[DEFAULT_MARKET];
}
export function getMarketYelpReviewLink(market: Maybe<string>) {
  return getMarketPublicReviewData(market).YELP;
}
export function getMarketGoogleReviewLink(market: Maybe<string>) {
  return getMarketPublicReviewData(market).GOOGLE;
}
export function getMarketNextdoorReviewLink(market: Maybe<string>) {
  return getMarketPublicReviewData(market).NEXTDOOR;
}
const CustomerSupportPickupHappiness = ['Happy', 'Neutral', 'Unhappy', 'Unknown'];
const DriverPickupHappiness = [
  'Very Happy',
  'Happy',
  'Neutral',
  'Unhappy',
  'Very Unhappy',
  'Unknown',
];
const MultipleReasons = [
  'New Job',
  'New Home',
  'Death',
  'Divorce',
  'Marriage',
  'Baby',
  'Downsizing',
  'Retirement',
];
const HearOfUs = [
  'Google',
  'Facebook',
  'Yelp',
  'Real Estate',
  'Organizer',
  'Property Manager',
  'Mover',
  'Stager / Decorator',
  'Friend',
  'Valpak Mailer',
];
const Type = ['Residential', 'Commercial', 'Storage'];
const WhyNot = [
  'Price',
  'Resale assessment',
  'Slow response',
  'Availability',
  'Insurance',
  'Missing service',
];
const ServiceUsed = [
  'Sell themselves',
  'City Pickup',
  'Junk hauler',
  'Donation: Salvation, Goodwill, etc',
  'Mover: Lugg, etc',
  'Storage',
  'Consignment store',
  'Auction house',
  'Decided to keep',
  'Other',
];
const Why = [
  'Getting money back for consignment items',
  'Remoov took care of all your unwanted items in one pickup',
  'You needed to clear out space',
  'Finding a new home for your items',
  'Quick turnaround for the pickup',
];
const Age = ['18-25', '26-35', '36-45', '46-55', '56-65', '65+'];
const Gender = ['Male', 'Female', 'Other'];
const CUSTOMER_SUPPORT_PICKUP_HAPPINESS = 'Customer support pickup happiness';
const DRIVER_CUSTOMER_FEEDBACK = 'Client satisfaction review';
const REASON = 'Reason';
const HEAR_FROM_US = 'Hear from us';
const CLIENT_TYPE = 'Client Type';
const WHY_NOT = 'Why Not';
const SERVICE_USED = 'Service used';
const AGE = 'Age';
const GENDER = 'Gender';
const PickupReviewKeys = {
  CUSTOMER_SUPPORT_PICKUP_HAPPINESS,
  DRIVER_CUSTOMER_FEEDBACK,
  REASON,
  HEAR_FROM_US,
  CLIENT_TYPE,
  WHY_NOT,
  SERVICE_USED,
  AGE,
  GENDER,
};
const HOW = 'How did you hear about Remoov?';
const WHY = 'Why did you decide to use Remoov?';
const SATISFACTION = 'How satisfied are you with our service?';
const IMPROVEMENT = 'How could we improve our service?';
const LIKE_MOST = 'What did you like most about our service?';
const PICKUP_TEAM_RATING = 'How was our pick up team today?';
const OVERALL_EXPERIENCE_RATING = 'How was your overall experience with Remoov?';
const ADDITIONAL_FEEDBACK = 'ADDITIONAL_FEEDBACK';
const ADDITIONAL_FEEDBACK_NEWWEBSITE = 'Would you like to leave more feedback?';
const CustomerReviewKeys = {
  HOW,
  WHY,
  SATISFACTION,
  IMPROVEMENT,
  LIKE_MOST,
  PICKUP_TEAM_RATING,
  OVERALL_EXPERIENCE_RATING,
  ADDITIONAL_FEEDBACK,
  ADDITIONAL_FEEDBACK_NEWWEBSITE,
};
export const PickupReview = {
  CustomerSupportPickupHappiness,
  DriverPickupHappiness,
  Reason,
  MultipleReasons,
  HearOfUs,
  Type,
  WhyNot,
  ServiceUsed,
  Why,
  CustomerReviewKeys,
  PickupReviewKeys,
  Age,
  Gender,
};
export const EMPTY_PICKUP_SURVEY = mapObjIndexed(
  (_v, k) => ([PICKUP_TEAM_RATING, OVERALL_EXPERIENCE_RATING].includes(k) ? null : ''),
  CustomerReviewKeys,
);
export const EMPTY_PICKUP_REVIEW = mapObjIndexed(() => '', PickupReviewKeys);
export function isHappySurvey(events: Event[]) {
  const survey = last(events.filter(propEq('event_type', 'submitPickupSurvey')))?.args?.['survey'];
  return (
    (survey?.[PICKUP_TEAM_RATING] === 4 || survey?.[PICKUP_TEAM_RATING] === 5) &&
    (survey?.[OVERALL_EXPERIENCE_RATING] === 4 || survey?.[OVERALL_EXPERIENCE_RATING] === 5)
  );
}
export function checkIfAlreadyclickedOnPublicReviewButton(events: Event[]) {
  return events
    .filter(propEq('event_type', 'frontendFeedback'))
    .filter(e => {
      return Object.values(SocialMediaPublicReviewMetricName).includes(e.args?.['metricName']);
    })
    .some(Boolean);
}
export function shouldRequestPublicReviewInCustomerDashboard({
  pickups,
  events,
}: {
  pickups: FlatPickup[];
  events: Event[];
}) {
  const hasPickupInRange =
    pickups
      .filter(
        pickup =>
          !!pickup.pickup_date &&
          isBetweenOrEqual(pickup.pickup_date, addDays(isoNow(), -14), isoNow()),
      )
      .filter(Boolean).length > 0;
  const checkHappySurvey = isHappySurvey(events);
  const alreadyClickedOnPublicReviewButton = checkIfAlreadyclickedOnPublicReviewButton(events);
  return hasPickupInRange && checkHappySurvey && !alreadyClickedOnPublicReviewButton;
}
